/**
 * 페이지 이미지 hook — react-query 기반.
 *
 * 책임:
 *   - react-query cache 에 Blob 보관 (PDF 페이지 이미지는 immutable → staleTime ∞)
 *   - cache 의 Blob 으로부터 ObjectURL 을 컴포넌트별로 안전하게 생성/해제
 *   - 인접 페이지 prefetch (다음/이전 페이지 즉시성) 를 위한 `usePrefetchPageImage` 제공
 *
 * 메모리 정책:
 *   - 한 페이지 PNG ≈ 1~3 MB. 보통 invoice 는 1~3 페이지 → 무시할 수준.
 *   - 같은 jobId/fileId/page 에 대해 여러 컴포넌트가 동시에 mount 되어도 query cache 1개만 유지.
 *   - ObjectURL 은 컴포넌트마다 만들고, 해당 컴포넌트 unmount 시 revoke.
 *
 * 책임 외:
 *   - 페이지 수 조회 (`usePagesMeta`)
 *   - 줌/스크롤/네비/다운로드 (`PdfPreviewPanel`)
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError } from "@/lib/api/client";
import { getPageImageBlob } from "@/lib/api/file_images";

export const PAGE_IMAGE_QUERY_KEY = (jobId: string, fileId: string, page: number) =>
  ["page-image", jobId, fileId, page] as const;

export interface UsePageImageState {
  url: string | null;
  isLoading: boolean;
  error: ApiError | Error | null;
}

export function usePageImage(jobId: string, fileId: string, page: number): UsePageImageState {
  const query = useQuery<Blob, ApiError | Error>({
    queryKey: PAGE_IMAGE_QUERY_KEY(jobId, fileId, page),
    queryFn: ({ signal }) => getPageImageBlob(jobId, fileId, page, { signal }),
    enabled: !!jobId && !!fileId && page >= 1,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000, // 5분 동안 cache 유지 → 뒤로가기 즉시성 ↑
    retry: 1,
  });

  // Blob → ObjectURL (컴포넌트 단위 lifecycle)
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(query.data);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
    };
  }, [query.data]);

  return {
    url,
    isLoading: query.isLoading || query.isFetching,
    error: query.error ?? null,
  };
}

/**
 * 인접 페이지를 silently 미리 받는 prefetcher.
 *
 * 사용:
 *   const prefetch = usePrefetchPageImage();
 *   useEffect(() => {
 *     prefetch(jobId, fileId, page + 1);
 *     prefetch(jobId, fileId, page - 1);
 *   }, [page]);
 */
export function usePrefetchPageImage() {
  const queryClient = useQueryClient();
  return (jobId: string, fileId: string, page: number) => {
    if (!jobId || !fileId || page < 1) return;
    queryClient.prefetchQuery({
      queryKey: PAGE_IMAGE_QUERY_KEY(jobId, fileId, page),
      queryFn: ({ signal }) => getPageImageBlob(jobId, fileId, page, { signal }),
      staleTime: Number.POSITIVE_INFINITY,
    });
  };
}
